import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { spaceOf } from "@/lib/spaces";
import { SpaceAvatar } from "@/components/SpaceAvatar";
import { BlurInput, ColorInput, Pick, Row, Section } from "@/components/settings/Controls";

const THEMES = [
  { value: "midnight", label: "Midnight (default)" }, { value: "deep", label: "Deep space" },
  { value: "atom", label: "Atom blue" }, { value: "forest", label: "Forest" }, { value: "sunset", label: "Sunset" },
];
const SWATCHES = ["#f59e0b", "#06b6d4", "#ec4899", "#10b981", "#8b5cf6", "#ef4444", "#84cc16", "#f97316"];
const EMOJIS = ["", "😎", "🦊", "🚀", "🐙", "🦄", "🧙", "🐸", "☕", "🔥"];

const PasswordForm = () => {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("The new passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password changed");
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
    return undefined;
  };
  return (
    <form onSubmit={submit} className="grid gap-3 py-4 sm:grid-cols-3" data-testid="password-form">
      <Input type="password" required placeholder="Current password" value={f.current_password} onChange={set("current_password")} data-testid="password-current" />
      <Input type="password" required minLength={8} placeholder="New password (8+ characters)" value={f.new_password} onChange={set("new_password")} data-testid="password-new" />
      <Input type="password" required placeholder="Confirm new password" value={f.confirm} onChange={set("confirm")} data-testid="password-confirm" />
      <button disabled={busy} className="flex h-10 items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-white disabled:opacity-60 sm:col-start-3" data-testid="password-submit">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Change password
      </button>
    </form>
  );
};

export const AccountTab = () => {
  const { user } = useAuth();
  const { prefs, savePrefs, reloadMembers } = useSettings();
  const me = spaceOf(user.username);
  const saveMember = async (patch) => {
    try {
      await api.put(`/settings/members/${user.username}`, { name: me.name, email: me.email, ...patch });
      await reloadMembers();
      toast.success("Profile saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      <Section title="Profile" hint="How you appear across the dashboard, tickets and TV wallboard." testid="settings-profile">
        <Row label="Preview"><SpaceAvatar space={user.username} size="lg" /><span className="font-semibold" style={{ color: me.color }}>{me.name}</span></Row>
        <Row label="Display name"><BlurInput value={me.name} onSave={(name) => saveMember({ name })} testid="profile-name-input" /></Row>
        <Row label="Email for reminders"><BlurInput type="email" value={me.email} onSave={(email) => saveMember({ email })} testid="profile-email-input" className="w-64" /></Row>
        <Row label="Your colour" hint="Used for your space, avatar and wallboard card.">
          {SWATCHES.map((c) => <button key={c} type="button" onClick={() => savePrefs({ color: c })} className={`h-7 w-7 rounded-full transition-transform hover:scale-110 ${me.color === c ? "ring-2 ring-white ring-offset-2 ring-offset-background" : ""}`} style={{ background: c }} data-testid={`profile-color-${c.slice(1)}`} />)}
          <ColorInput value={me.color} onSave={(color) => savePrefs({ color })} testid="profile-color-custom" />
        </Row>
        <Row label="Avatar emoji" hint="Shown instead of your initial.">
          {EMOJIS.map((e) => <button key={e || "none"} type="button" onClick={() => savePrefs({ emoji: e })} className={`flex h-8 w-8 items-center justify-center rounded-full border text-base transition-colors hover:bg-white/5 ${prefs.emoji === e ? "border-white/60 bg-white/10" : ""}`} data-testid={`profile-emoji-${e ? EMOJIS.indexOf(e) : "none"}`}>{e || me.name[0]}</button>)}
        </Row>
        <Row label="Theme"><Pick value={prefs.theme} onChange={(theme) => savePrefs({ theme })} options={THEMES} testid="profile-theme-select" /></Row>
      </Section>
      <Section title="Password" hint="Use at least 8 characters. You'll stay signed in on this device." testid="settings-password"><PasswordForm /></Section>
    </div>
  );
};
